/**
 * One /amazon/reports/cached request for current + previous (+ today) periods.
 * Same API path and params shape — fewer round trips for Campaign Manager & Bulk Ops.
 */

import { amazonApi } from '../services/api';
import { extractReportRows } from './reportMetricsUtils';
import {
  isDateInRange,
  previousPeriodBounds,
  toLocalISODate,
} from './dateRangeUtils';

function sliceDate(a, b) {
  const sa = String(a || '').slice(0, 10);
  const sb = String(b || '').slice(0, 10);
  return sa <= sb ? sa : sb;
}

function maxDate(a, b) {
  const sa = String(a || '').slice(0, 10);
  const sb = String(b || '').slice(0, 10);
  return sa >= sb ? sa : sb;
}

function aggregatePeriod(rows, startDate, endDate) {
  const metrics = {};
  (rows || []).forEach((row) => {
    if (!isDateInRange(row.date, startDate, endDate)) return;
    const campId = String(row.campaignId || '');
    if (!campId) return;
    if (!metrics[campId]) {
      metrics[campId] = { impressions: 0, clicks: 0, spend: 0, sales: 0, orders: 0 };
    }
    metrics[campId].impressions += Number(row.impressions) || 0;
    metrics[campId].clicks += Number(row.clicks) || 0;
    metrics[campId].spend += Number(row.cost ?? row.spend) || 0;
    metrics[campId].sales += Number(row.sales14d ?? row.sales) || 0;
    metrics[campId].orders += Number(row.purchases14d ?? row.orders) || 0;
  });
  return metrics;
}

function aggregateTodaySpend(rows, todayStr) {
  const bucket = {};
  (rows || []).forEach((row) => {
    if (!isDateInRange(row.date, todayStr, todayStr)) return;
    const campId = String(row.campaignId || '');
    if (!campId) return;
    bucket[campId] = {
      todaysSpend: (bucket[campId]?.todaysSpend || 0) + (Number(row.cost ?? row.spend) || 0),
    };
  });
  return bucket;
}

const inFlight = new Map();

function bundleKey(profileId, fetchStart, fetchEnd) {
  return `${profileId}|${fetchStart}|${fetchEnd}`;
}

/**
 * @returns {Promise<{ rows: Array, loaded: boolean, response: object }>}
 */
async function fetchCombinedRows(profileId, fetchStart, fetchEnd, { timeoutMs = 20000 } = {}) {
  const key = bundleKey(profileId, fetchStart, fetchEnd);
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const job = (async () => {
    try {
      const reportPromise = amazonApi.getCachedReport(profileId, fetchStart, fetchEnd);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Report request timeout')), timeoutMs)
      );
      const reportResponse = await Promise.race([reportPromise, timeoutPromise]);
      if (reportResponse.data?.success && reportResponse.data?.data) {
        const rows = extractReportRows(reportResponse.data.data);
        return { rows, loaded: true, response: reportResponse };
      }
      return { rows: [], loaded: false, response: reportResponse };
    } catch (error) {
      return { rows: [], loaded: false, error };
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, job);
  return job;
}

/**
 * Campaign Manager / Bulk: current period metrics, previous period, and today spend in one cached-report call.
 */
export async function loadCampaignMetricsBundle(
  profileId,
  startDate,
  endDate,
  dateRange,
  { includePrevious = true, includeToday = true, timeoutMs = 20000 } = {}
) {
  const todayStr = toLocalISODate();
  let fetchStart = startDate;
  let fetchEnd = endDate;

  let previousStart;
  let previousEnd;
  if (includePrevious) {
    const prev = previousPeriodBounds(startDate, endDate, dateRange);
    previousStart = prev.startDate;
    previousEnd = prev.endDate;
    fetchStart = sliceDate(fetchStart, previousStart);
  }

  if (includeToday && isDateInRange(todayStr, startDate, endDate)) {
    fetchEnd = maxDate(fetchEnd, todayStr);
  } else if (includeToday) {
    fetchEnd = maxDate(fetchEnd, todayStr);
    fetchStart = sliceDate(fetchStart, todayStr);
  }

  const { rows, loaded, response } = await fetchCombinedRows(
    profileId,
    fetchStart,
    fetchEnd,
    { timeoutMs }
  );

  const currentMetrics = aggregatePeriod(rows, startDate, endDate);
  const previousMetrics = includePrevious && previousStart
    ? aggregatePeriod(rows, previousStart, previousEnd)
    : {};
  const todaySpend = includeToday ? aggregateTodaySpend(rows, todayStr) : {};

  return {
    loaded,
    rows,
    response,
    currentMetrics,
    previousMetrics,
    todaySpend,
    todayStr,
    ranges: { fetchStart, fetchEnd, startDate, endDate, previousStart, previousEnd },
  };
}

/** Rows filtered to the active period (for daily charts / accumulation). */
export function filterBundleRowsToPeriod(rows, startDate, endDate) {
  return (rows || []).filter((row) => isDateInRange(row.date, startDate, endDate));
}
